import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateLLMModelSpecificationRequest,
  LLMModelSpecificationReorderRequest,
  ModelType,
  QueryKey,
} from 'api/model.ts';
import { toast } from 'react-toastify';
import {
  createModelSpecification,
  getModelSpecifications,
  refreshModelSpecifications,
  removeModelSpecification,
  reorderModelSpecifications,
} from './requests';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.ModelSpecifications;

const useModelSpecifications = (provider?: ModelType, enabled = true) => {
  const queryClient = useQueryClient();

  const { data: modelSpecifications, isLoading } = useQuery({
    queryKey: [queryKey, provider ?? 'all'],
    queryFn: () => getModelSpecifications(provider),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });

  const create = useMutation({
    mutationFn: (modelSpecification: CreateLLMModelSpecificationRequest) =>
      createModelSpecification(modelSpecification),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model specification successfully created');
    },
    onError: errorHandler('Creating model specification failed'),
  });

  const refresh = useMutation({
    mutationFn: () => refreshModelSpecifications(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model specifications successfully refreshed');
    },
    onError: errorHandler('Refreshing model specifications failed'),
  });

  const reorder = useMutation({
    mutationFn: ({ provider, payload }: { provider: ModelType; payload: LLMModelSpecificationReorderRequest }) =>
      reorderModelSpecifications({ provider, payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model specifications successfully reordered');
    },
    onError: errorHandler('Reordering model specifications failed'),
  });

  const remove = useMutation({
    mutationFn: ({ provider, name }: { provider: ModelType; name: string }) =>
      removeModelSpecification({ provider, name }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Model specification successfully removed');
    },
    onError: errorHandler('Removing model specification failed'),
  });

  const clearQueryKey = () => {
    void queryClient.invalidateQueries({ queryKey: [queryKey] });
  };

  return { modelSpecifications, isLoading, create, refresh, reorder, remove, clearQueryKey };
};

export { queryKey };
export default useModelSpecifications;
